/**
 * Send view: recipient and amount, review, sign and submit. Templates: send-form-template and
 * send-review-template. The result of the last submission is a panel above the form while txid is set;
 * it closes from its header, on the next review and when the user leaves the tab.
 */

import ko from 'knockout';
import { ModelBase } from './base';
import type { WalletModel } from './wallet';
import type { PendingTransfer } from '../transactions';
import { FEE, addAmounts, reviewTransfer, submitTransfer } from '../transactions';

export class SendModel extends ModelBase {
    readonly fee = FEE;

    // form
    readonly to = ko.observable('');
    readonly amount = ko.observable('');
    readonly availableText = ko.pureComputed(() => this.wallet.account()?.balance ?? '—');

    // review and result
    readonly pending = ko.observable<PendingTransfer | null>(null);
    readonly totalText = ko.pureComputed(() => {
        const p = this.pending();
        return p ? addAmounts(p.amount, FEE) : '';
    });
    readonly busy = ko.observable(false);
    /** The last submission's transaction id; the result panel is shown while it is set. */
    readonly txid = ko.observable('');
    /** The node answered that the transaction is in a block; otherwise it is still being processed. */
    readonly included = ko.observable(false);

    constructor(private readonly wallet: WalletModel) {
        super();
        this.template('send-form-template');
    }

    review(): void {
        this.hideErrors();
        this.dismissResult();
        try {
            this.pending(reviewTransfer({ to: this.to(), amount: this.amount(), balance: this.wallet.account()?.balance ?? null }));
            this.template('send-review-template');
        } catch (err) {
            this.displayErrors(err);
        }
    }

    async confirm(): Promise<void> {
        const pending = this.pending();
        if (!pending) return;
        this.hideErrors();
        this.busy(true);
        try {
            const result = await submitTransfer(pending);
            this.txid(result.txid);
            this.included(result.status === 'included');
            this.pending(null);
            this.to('');
            this.amount('');
            this.template('send-form-template');
            this.wallet.expectAccountChange();
        } catch (err) {
            this.displayErrors(err);
        } finally {
            this.busy(false);
        }
    }

    cancel(): void {
        this.pending(null);
        this.template('send-form-template');
    }

    /** Closes the result panel. */
    dismissResult(): void {
        this.txid('');
        this.included(false);
    }

    /** The user left the tab or locked the wallet: no result, no error, and a review goes back to the form with its values. */
    leave(): void {
        if (this.busy()) return;
        this.dismissResult();
        this.hideErrors();
        this.cancel();
    }
}
